import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Phone, Mail, Globe } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  "Political Election Campaign",
  "Corporate PR",
  "Institutional PR",
  "Data Analysis & War Room",
  "Page Handling & Social",
  "Crisis & Reputation",
  "Other",
];

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: services[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please complete all fields before sending.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSent(true);
      toast.success("Brief received. A partner will be in touch within 24 hours.");
      setForm({ name: "", phone: "", email: "", service: services[0], message: "" });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Could not send your brief. Please try again or call us directly."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="section-gutter py-20 md:py-32 border-t border-black/10"
    >
      <div className="grid grid-cols-12 gap-6 md:gap-10">
        {/* Left — heading & contacts */}
        <div className="col-span-12 md:col-span-5">
          <div className="overline text-black/60">Chapter 07 — Brief Us</div>
          <h2 className="font-serif-display text-5xl md:text-6xl lg:text-7xl mt-6 leading-[0.9]">
            Tell us
            <br />
            <em>the story you</em>
            <br />
            need told.
          </h2>

          <div className="mt-10 space-y-5">
            <a
              href="tel:+919847659129"
              data-testid="contact-phone"
              className="flex items-center gap-4 group border-b border-black/10 pb-4"
            >
              <Phone size={18} />
              <div>
                <div className="overline text-black/50">Direct Line</div>
                <div className="font-serif-display text-2xl md:text-3xl group-hover:italic">
                  +91 98476 59129
                </div>
              </div>
            </a>
            <a
              href="mailto:info@blackandwhitekampany.com"
              data-testid="contact-email"
              className="flex items-center gap-4 group border-b border-black/10 pb-4"
            >
              <Mail size={18} />
              <div>
                <div className="overline text-black/50">Studio</div>
                <div className="font-serif-display text-2xl md:text-3xl group-hover:italic">
                  info@blackandwhitekampany.com
                </div>
              </div>
            </a>
            <a
              href="https://www.blackandwhitekampany.com"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-web"
              className="flex items-center gap-4 group"
            >
              <Globe size={18} />
              <div>
                <div className="overline text-black/50">Web</div>
                <div className="font-serif-display text-2xl md:text-3xl group-hover:italic">
                  blackandwhitekampany.com
                </div>
              </div>
            </a>
          </div>
        </div>

        {/* Right — form */}
        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          data-testid="contact-form"
          className="col-span-12 md:col-span-6 md:col-start-7 border border-black p-6 md:p-10 bg-white"
        >
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <span className="overline">Form №07 / The Brief</span>
            <span className="font-serif-display italic text-black/40">confidential</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-x-8 md:gap-y-6">
            <label className="block md:col-span-1">
              <span className="overline text-black/50">Name</span>
              <input
                value={form.name}
                onChange={onChange("name")}
                data-testid="form-name"
                className="brutal-input mt-2"
                placeholder="Your full name"
                autoComplete="name"
              />
            </label>
            <label className="block md:col-span-1">
              <span className="overline text-black/50">Phone</span>
              <input
                value={form.phone}
                onChange={onChange("phone")}
                data-testid="form-phone"
                className="brutal-input mt-2"
                placeholder="+91 ·····"
                autoComplete="tel"
              />
            </label>
            <label className="block md:col-span-1">
              <span className="overline text-black/50">Email</span>
              <input
                value={form.email}
                onChange={onChange("email")}
                data-testid="form-email"
                type="email"
                className="brutal-input mt-2"
                placeholder="you@organisation.in"
                autoComplete="email"
              />
            </label>
            <label className="block md:col-span-1">
              <span className="overline text-black/50">Service</span>
              <select
                value={form.service}
                onChange={onChange("service")}
                data-testid="form-service"
                className="brutal-input mt-2 appearance-none bg-transparent"
              >
                {services.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label className="block md:col-span-2">
              <span className="overline text-black/50">The Brief</span>
              <textarea
                value={form.message}
                onChange={onChange("message")}
                data-testid="form-message"
                rows={4}
                className="brutal-input mt-2 resize-none"
                placeholder="Tell us about the mandate, the timeline and the stakes."
              />
            </label>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
            <p className="text-xs text-black/60 max-w-sm">
              By submitting, you agree to a confidential discovery call with a senior partner.
            </p>
            <button
              type="submit"
              disabled={loading || sent}
              data-testid="form-submit"
              className="btn-invert px-7 py-4 text-[0.72rem] uppercase tracking-[0.22em] font-bold inline-flex items-center gap-2 disabled:opacity-60"
            >
              {sent ? "Brief Received" : loading ? "Sending…" : "Send Brief"}
              <ArrowUpRight size={16} />
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
